import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { useCurrentCabinet } from '@/hooks/cabinet/useCabinetCore';
import { trierCandidatsParDate } from './trierCandidatsParDate';

export interface AttackedDecisionCandidate {
  id: string;
  name: string;
  document_type?: string | null;
  /** Date intrinsèque de la pièce (colonne SQL `date`). */
  document_date?: string | null;
}

/** Cache identity includes the canonical attacked-decision scope. */
export function attackedDecisionQueryKey(cabinetId: string | undefined, caseId: string | undefined) {
  return ['case-documents', cabinetId, caseId, 'attacked_decision', 'procedures', false] as const;
}

/**
 * Loads current-dossier procedure files; syntheses never qualify as an attacked decision.
 *
 * The list is sorted by intrinsic document date (descending, nulls last) — the most
 * recent judgment or decree appears first in the picker.
 */
export function useAttackedDecisionCandidates(caseId: string | undefined, enabled: boolean) {
  const currentCabinet = useCurrentCabinet();
  const cabinetId = currentCabinet.data?.id;
  return useQuery({
    queryKey: attackedDecisionQueryKey(cabinetId, caseId),
    queryFn: async () => {
      if (!caseId || !cabinetId) {
        throw new Error(`Cannot load attacked decisions: missing server scope cabinetId=${cabinetId ?? '<missing>'}, caseId=${caseId ?? '<missing>'}.`);
      }
      const candidates = await apiClient.get<AttackedDecisionCandidate[]>(
        `/cases/${caseId}/documents?category=procedures&is_folder=false`,
      );
      return candidates.filter((candidate) => candidate.document_type !== 'synthesis');
    },
    // `select` runs on every cache read, including pre-seeded Storybook fixtures —
    // keeps the sort consistent whether the data comes from the network or a test.
    select: (data) => trierCandidatsParDate(data),
    enabled: enabled && !!caseId && !!cabinetId,
    staleTime: 30_000,
  });
}
